/**
 * @file api.ts
 * @description Type-safe API client for interacting with backend services.
 * Provides HTTP API methods for character management, module browsing, and more.
 * Authentication is handled via Firebase tokens.
 */

import { auth } from './firebase';
import { emitSessionExpired } from './session-expired-events';
import { z } from 'zod';
import type {
  CharacterPermanent,
  CharacterSession,
  ConditionEntry,
  FriendInfo,
} from '@danda/shared';
import { MAX_CACHE_SIZE } from '@danda/shared';

// =============================================================================
// Types
// =============================================================================

/** Module info as returned by the API (matches Rust Module struct serialization) */
export interface Module {
  id: string;
  name: string;
  shortDescription: string;
  level: { min: number; max: number };
  heroImage?: string;
  heroVideo?: string;
}

/**
 * Full character data returned by GET /api/characters/:id
 * Includes both permanent and session data
 */
export interface CharacterFullData {
  charId: string;
  permanent: CharacterPermanent;
  session: CharacterSession;
}

export interface CharacterCreationSession {
  id: string;
  userId: string;
  status: 'active' | 'completed' | 'cancelled';
  currentStep: string;
  characterData: Partial<CharacterPermanent>;
  createdAt: string;
  updatedAt: string;
}

interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
  retries?: number;
  timeout?: number;
  /** Skip sign-out on 401 - use for endpoints that may legitimately fail auth */
  skipSignOutOn401?: boolean;
  /** Cache TTL in ms for GET responses (default: 5 minutes) */
  cacheTtl?: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// =============================================================================
// Schemas
// =============================================================================

// Basic schemas - using passthrough to allow flexibility while validating core fields
const CharacterSchema = z.looseObject({
  charId: z.string().optional(),
  name: z.string(),
});

const CharacterFullDataSchema = z.object({
  charId: z.string(),
  permanent: CharacterSchema,
  session: z.record(z.string(), z.unknown()),
});

const GetCharactersResponseSchema = z.object({
  characters: z.array(CharacterSchema),
  total: z.number(),
});

const ModuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  shortDescription: z.string(),
  level: z.object({
    min: z.number(),
    max: z.number(),
  }),
  heroImage: z.string().optional(),
  heroVideo: z.string().optional(),
}) satisfies z.ZodType<Module>;

const StatusSchema = z.object({
  status: z.string(),
  timestamp: z.string(),
  version: z.string(),
});

/** Resume-card context from `/api/session/resume-context`. */
export const ResumeContextSchema = z.object({
  state: z.enum(['live', 'ended_incomplete', 'none']),
  activeSessionId: z.string().optional(),
  partyId: z.string().optional(),
  moduleId: z.string().optional(),
  moduleName: z.string().optional(),
  sceneName: z.string().optional(),
  /** Raw scene media path (resolve with getModuleMediaUrl-style helpers). */
  sceneMediaPath: z.string().optional(),
  encounterType: z.string().optional(),
  round: z.number().optional(),
  lastSession: z
    .object({
      finalGameTimeSeconds: z.number().optional(),
      summary: z.string().optional(),
      moduleId: z.string().optional(),
      moduleName: z.string().optional(),
      reason: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

export type ResumeContext = z.infer<typeof ResumeContextSchema>;

/** Per-account disconnect setting; wire values match the Rust `DropBehavior` enum. */
export type DropBehavior = 'become_npc' | 'leave_scene';

/** Per-reward claim state for the Profile-tab Rewards block. */
const RewardStateSchema = z.enum(['unclaimed', 'pending', 'credited']);
export type RewardState = z.infer<typeof RewardStateSchema>;

const MyProfileSchema = z.looseObject({
  email: z.string().optional().default(''),
  displayName: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  passkeyRegistered: z.boolean().optional().default(false),
  charId: z.string().nullable().optional(),
  createdAt: z.string().optional().default(''),
  pmTokenBalance: z.number().optional().default(0),
  isAdmin: z.boolean().optional().default(false),
  dropBehavior: z.enum(['become_npc', 'leave_scene']).optional(),
  combatHighlightsEnabled: z.boolean().optional().default(true),
});

export type MyProfile = z.infer<typeof MyProfileSchema>;

// Admin schemas
const AdminUserInfoSchema = z.object({
  uid: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  charId: z.string().nullable(),
  pmTokenBalance: z.number(),
  isAdmin: z.boolean(),
  lastLoginAt: z.string().nullable().optional(),
});

const PartyMemberSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarEmoji: z.string().optional(),
  avatarUrl: z.string().optional(),
  class: z.string().optional(),
  level: z.number().optional(),
  ownerId: z.string().optional(),
});

const AdminPartySchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  memberCharacterIds: z.array(z.string()),
  members: z.array(PartyMemberSummarySchema).default([]),
  leaderCharacterId: z.string().nullable().optional(),
  selectedModuleId: z.string().nullable().optional(),
  selectedModuleName: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  activeGameSessionId: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  partyImageUrl: z.string().optional(),
  createdAt: z.string().optional(),
});

const PlayerCharSummarySchema = z.object({
  charId: z.string(),
  ownerUserId: z.string(),
  name: z.string(),
  level: z.number().nullable(),
  species: z.string().nullable(),
  class: z.string().nullable(),
  avatarEmoji: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  portraitUrl: z.string().nullable(),
  createdAt: z.string().nullable(),
});

const ErrorEntrySchema = z.object({
  timestamp: z.string(),
  level: z.string(),
  target: z.string(),
  message: z.string(),
});

const AdminOverviewSchema = z.object({
  timestamp: z.string(),
  users: z.array(AdminUserInfoSchema),
  parties: z.array(AdminPartySchema),
  playerCharacters: z.array(PlayerCharSummarySchema).default([]),
  llmSpend: z.object({
    todayUsd: z.number(),
    recentCalls: z.array(z.unknown()),
  }),
  recentErrors: z.array(ErrorEntrySchema),
});

const AdminSessionInfoSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  playerUserIds: z.array(z.string()),
});

const AdminGameStatusSchema = z.object({
  timestamp: z.string(),
  version: z.string(),
  environment: z.string(),
  stats: z.object({
    activeSessions: z.number(),
    maxSessions: z.number(),
    capacityPercent: z.number(),
  }),
  features: z.object({
    websocket: z.boolean(),
    aiEngines: z.boolean(),
    search: z.boolean(),
    firestore: z.boolean(),
  }),
  sessions: z.array(AdminSessionInfoSchema),
  recentErrors: z.array(ErrorEntrySchema),
});

const ServiceStatusSchema = z.object({
  name: z.string(),
  status: z.string(),
  url: z.string().optional(),
});

const ServiceHealthSchema = z.array(ServiceStatusSchema);

export type AdminUserInfo = z.infer<typeof AdminUserInfoSchema>;
export type AdminParty = z.infer<typeof AdminPartySchema>;
export type PartyMemberSummary = z.infer<typeof PartyMemberSummarySchema>;
export type PlayerCharSummary = z.infer<typeof PlayerCharSummarySchema>;
export type ErrorEntry = z.infer<typeof ErrorEntrySchema>;
export type AdminOverviewResponse = z.infer<typeof AdminOverviewSchema>;
export type AdminSessionInfo = z.infer<typeof AdminSessionInfoSchema>;
export type AdminGameStatusResponse = z.infer<typeof AdminGameStatusSchema>;
export type ServiceHealthResponse = z.infer<typeof ServiceHealthSchema>;

// =============================================================================
// API Client
// =============================================================================

class ApiClient {
  private baseUrl: string;
  private cache = new Map<string, CacheEntry<unknown>>();
  private requestQueue = new Map<string, Promise<unknown>>();

  constructor() {
    // Use environment variable or derive from current location
    this.baseUrl = import.meta.env.VITE_API_URL || '';
  }

  // ===========================================================================
  // Core Request Methods
  // ===========================================================================

  private async fetchJson<T>(
    url: string,
    schema: z.ZodType<T>,
    config: RequestConfig = {}
  ): Promise<T> {
    const {
      skipAuth = false,
      retries = 3,
      timeout = 10000,
      skipSignOutOn401 = false,
      cacheTtl = 1000,
      ...options
    } = config;

    // Create cache key
    const cacheKey = `${options.method || 'GET'}:${url}:${JSON.stringify(options.body)}`;

    // Check cache for GET requests
    if (options.method === undefined || options.method === 'GET') {
      const cached = this.getCached<T>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Request deduplication - check for existing in-flight request
    // Safe from race conditions because no await between check and set below
    const existingRequest = this.requestQueue.get(cacheKey);
    if (existingRequest) {
      return existingRequest as Promise<T>;
    }

    // Define request function inline (no network call yet)
    const makeRequest = async (attempt: number = 1): Promise<T> => {
      const controller = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | null = setTimeout(
        () => controller.abort(),
        timeout
      );

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          ...(options.headers as Record<string, string>),
        };

        // Add auth headers
        if (!skipAuth) {
          const authHeaders = await this.getAuthHeaders();
          Object.assign(headers, authHeaders);
        }

        const response = await fetch(`${this.baseUrl}${url}`, {
          ...options,
          headers,
          signal: controller.signal,
        });

        // Clear timeout immediately after fetch completes
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            message: response.statusText,
            statusCode: response.status,
          }));

          // Handle 401 Unauthorized - session expired/invalid token.
          // Per Inv 3, we do NOT sign the user out automatically. Emit a
          // session-expired event so the global overlay can attempt a silent
          // token refresh; only the user's explicit "Sign out" click runs
          // the real signOut path.
          //
          // `clearAll()` invalidates BOTH the response cache and the
          // in-flight request queue: post-refresh GETs re-fetch instead of
          // serving data we cached just before the token went stale, AND
          // any concurrent dedup subscribers get the rejection rather than
          // a stale resolved value from an in-flight pre-401 request.
          // Concurrent requests would have failed under the same expired
          // token anyway — making them reject is the correct outcome.
          if (response.status === 401 && !skipSignOutOn401) {
            console.warn('API 401 Unauthorized — emitting session-expired event');
            this.clearAll();
            emitSessionExpired('api-401');
            throw new Error('Session expired. Refreshing — please retry.');
          }

          // Handle 403 Forbidden - valid session but insufficient permissions
          // Do NOT sign out - this is a permission error, not an auth error
          // Examples: accessing another user's character, feature flags, ownership checks
          if (response.status === 403) {
            throw new Error(errorData.message || 'Access denied. You do not have permission.');
          }

          // Retry on server errors
          if (attempt < retries && response.status >= 500) {
            console.warn(`API request failed (attempt ${attempt}/${retries}), retrying...`);
            await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
            return makeRequest(attempt + 1);
          }

          // Retry on rate limit with exponential backoff
          if (attempt < retries && response.status === 429) {
            const retryAfter = response.headers.get('Retry-After');
            const parsedRetryAfter = retryAfter ? parseInt(retryAfter, 10) : NaN;
            // Guard against NaN from non-numeric Retry-After (e.g., date strings)
            const delay = Number.isFinite(parsedRetryAfter)
              ? parsedRetryAfter * 1000
              : Math.pow(2, attempt) * 1000 + Math.random() * 1000; // Jittered backoff
            console.warn(
              `API rate limited (attempt ${attempt}/${retries}), retrying in ${delay}ms...`
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            return makeRequest(attempt + 1);
          }

          throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        // Validate response data
        const result = schema.safeParse(data);
        if (!result.success) {
          console.error(`API Validation Error for ${url}:`, result.error);
          throw new Error(`Invalid API response from ${url}: ${result.error.message}`);
        }
        const validatedData = result.data;

        // Cache successful GET responses
        if (options.method === undefined || options.method === 'GET') {
          this.setCache(cacheKey, validatedData, cacheTtl);
        }

        return validatedData;
      } catch (error) {
        // Ensure timeout is cleared on error
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        // TypeError = connection-level failure (DNS, refused, offline). AbortError =
        // our own `timeout` fired without the server ever responding, functionally
        // the same "the request just didn't complete" case, so it gets the same retry.
        const isRetryableNetworkError =
          error instanceof TypeError ||
          (error instanceof DOMException && error.name === 'AbortError');
        if (attempt < retries && isRetryableNetworkError) {
          console.warn(`Network error (attempt ${attempt}/${retries}), retrying...`);
          await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          return makeRequest(attempt + 1);
        }
        throw error;
      }
    };

    const requestPromise = makeRequest();
    this.requestQueue.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.requestQueue.delete(cacheKey);
    }
  }

  private getCached<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  private setCache<T>(key: string, data: T, ttl: number): void {
    this.cache.set(key, { data, timestamp: Date.now(), ttl });

    // Evict oldest entry if cache exceeds max size (O(1) instead of O(n) cleanup)
    if (this.cache.size > MAX_CACHE_SIZE) {
      // Map maintains insertion order, so first key is oldest
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }
  }

  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear all client-side state (cache + inflight dedupe queue).
   * Useful on auth transitions to avoid cross-user leakage.
   */
  public clearAll(): void {
    this.cache.clear();
    this.requestQueue.clear();
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        return { Authorization: `Bearer ${token}` };
      }
    } catch (error) {
      console.warn('Auth not available:', error);
    }
    return {};
  }

  // ===========================================================================
  // Character API
  // ===========================================================================

  async getCharacters(): Promise<{ characters: CharacterPermanent[]; total: number }> {
    const result = await this.fetchJson('/api/characters', GetCharactersResponseSchema);
    return {
      total: result.total,
      characters: result.characters.map(({ charId, ...rest }) => ({
        ...(rest as Record<string, unknown>),
        id: charId,
      })) as CharacterPermanent[],
    };
  }

  /** Fast path: user profile → charId → character permanent + image URLs */
  async getMyCharacter(): Promise<CharacterPermanent | null> {
    const schema = z.object({
      character: z
        .looseObject({
          charId: z.string(),
          permanent: z.looseObject({ name: z.string() }),
        })
        .nullable(),
    });
    const result = await this.fetchJson('/api/me/character', schema);
    if (!result.character) return null;
    const { charId, permanent, session } = result.character as {
      charId: string;
      permanent: Record<string, unknown>;
      session?: Record<string, unknown>;
    };
    // Pull image URLs from session data into the returned object
    const avatarUrl = session?.avatar_url as string | undefined;
    const portraitUrl = session?.portrait_url as string | undefined;
    return {
      ...permanent,
      id: charId,
      ...(avatarUrl && { avatar_url: avatarUrl }),
      ...(portraitUrl && { portrait_url: portraitUrl }),
    } as CharacterPermanent;
  }

  async getCharacter(id: string): Promise<CharacterFullData> {
    const result = await this.fetchJson(`/api/characters/${id}`, CharacterFullDataSchema);
    const { charId, permanent, ...rest } = result as unknown as {
      charId: string;
      permanent: Record<string, unknown>;
      session: Record<string, unknown>;
    } & Record<string, unknown>;
    const { charId: _ignored, ...permanentWithoutCharId } = permanent;
    const sessionRecord = (result.session ?? {}) as Record<string, unknown>;
    const hp =
      typeof sessionRecord.hp === 'number' && Number.isFinite(sessionRecord.hp)
        ? sessionRecord.hp
        : 0;
    const conditions: ConditionEntry[] = Array.isArray(sessionRecord.conditions)
      ? sessionRecord.conditions.map((c) =>
          typeof c === 'string' ? { name: c } : (c as ConditionEntry)
        )
      : [];
    const session: CharacterSession = {
      ...sessionRecord,
      id: charId,
      name:
        typeof sessionRecord.name === 'string'
          ? sessionRecord.name
          : ((permanentWithoutCharId.name as string) ?? 'Unknown'),
      hp,
      conditions,
    };
    return {
      ...rest,
      charId,
      permanent: { ...permanentWithoutCharId, id: charId } as CharacterPermanent,
      session,
    };
  }

  async deleteCharacter(id: string): Promise<void> {
    await this.fetchJson(`/api/characters/${id}`, z.any(), { method: 'DELETE' });
  }

  // ===========================================================================
  // Module API
  // ===========================================================================

  async getModule(id: string): Promise<Module> {
    return this.fetchJson<Module>(`/api/modules/${id}`, ModuleSchema);
  }

  // ===========================================================================
  // Party API
  // ===========================================================================

  async getParty(): Promise<{ party: Record<string, unknown> | null }> {
    return this.fetchJson(
      '/api/party',
      z.object({
        party: z.record(z.string(), z.unknown()).nullable(),
      }),
      { retries: 2, skipSignOutOn401: true }
    );
  }

  // ===========================================================================
  // Friends API (settings dialog; works without a live lobby socket)
  // ===========================================================================

  async getFriends(): Promise<FriendInfo[]> {
    const res = await this.fetchJson(
      '/api/friends',
      z.object({
        friends: z.array(
          z.looseObject({
            userId: z.string(),
            displayName: z.string(),
            online: z.boolean(),
            inSession: z.boolean(),
            // Serialized with `#[serde(default)]`, so an older lobby-server
            // that predates the field omits it rather than sending false.
            inParty: z.boolean().default(false),
            avatarUrl: z.string().optional(),
            avatarEmoji: z.string().optional(),
            charId: z.string().optional(),
            charName: z.string().optional(),
            charLevel: z.number().optional(),
            charClass: z.string().optional(),
            charSpecies: z.string().optional(),
          })
        ),
      }),
      { retries: 2, skipSignOutOn401: true }
    );
    return res.friends as FriendInfo[];
  }

  /**
   * Mutual: the pair shares one friendship document, so neither side keeps the
   * other. Idempotent — an already-removed pair returns `alreadyRemoved`.
   *
   * `skipSignOutOn401` because this relays lobby-server's status verbatim: a
   * SERVICE_AUTH_TOKEN drift between the services would otherwise surface as a
   * bogus "session expired" sign-out.
   */
  async removeFriend(friendUserId: string): Promise<void> {
    await this.fetchJson(
      `/api/friends/${encodeURIComponent(friendUserId)}`,
      z.looseObject({ success: z.boolean().optional(), alreadyRemoved: z.boolean().optional() }),
      { method: 'DELETE', skipSignOutOn401: true }
    );
  }

  /**
   * Set the caller's party round-pacing mode (leader-only, pre-game only)
   * with no live lobby WebSocket required. Bridges through api-server to
   * lobby-server's leader/session-state gate, so it throws the same
   * rejection reasons the WS path does (not leader, session active, no
   * party) — surface `error.message` to the user.
   */
  async setPartyPacing(mode: 'turn_based' | 'real_time'): Promise<void> {
    await this.fetchJson('/api/party/pacing', z.looseObject({ success: z.boolean().optional() }), {
      method: 'PATCH',
      body: JSON.stringify({ mode }),
    });
  }

  // ===========================================================================
  // Session API
  // ===========================================================================

  /**
   * Check if user has an active game session
   * Fast endpoint for routing decisions - returns sessionId if in active game
   */
  async getActiveSession(): Promise<{ activeSessionId: string | null }> {
    return this.fetchJson(
      '/api/session/active',
      z.object({ activeSessionId: z.string().nullable() }),
      { retries: 2, timeout: 5000, skipSignOutOn401: true } // Don't sign out if auth fails - just return null
    );
  }

  async getJournal(): Promise<{ journal: Record<string, unknown> | null }> {
    return this.fetchJson(
      '/api/session/journal',
      z.object({ journal: z.record(z.string(), z.unknown()).nullable() }),
      { retries: 1, timeout: 10000 }
    );
  }

  /**
   * Resume context for the dashboard cliffhanger card: live vs
   * ended-incomplete state plus scene/recap decoration. Degrades to
   * `state: 'none'` server-side on any data problem.
   */
  async getResumeContext(): Promise<ResumeContext> {
    return this.fetchJson('/api/session/resume-context', ResumeContextSchema, {
      retries: 2,
      timeout: 5000,
      skipSignOutOn401: true,
    });
  }

  // ===========================================================================
  // Billing API
  // ===========================================================================

  /** Get user's PM token balance */
  async getPmBalance(opts?: { skipCache?: boolean }): Promise<{ pmTokenBalance: number }> {
    // Cache-buster query param ensures a fresh fetch after a Stripe purchase
    // success redirect — the standard 1s TTL cache would otherwise return the
    // stale pre-purchase balance for ~1s after the webhook lands.
    const url = opts?.skipCache ? `/api/billing/balance?_t=${Date.now()}` : '/api/billing/balance';
    return this.fetchJson(url, z.object({ pmTokenBalance: z.number() }), {
      retries: 1,
      timeout: 5000,
    });
  }

  /**
   * Create a Stripe Checkout Session for buying PM tokens.
   *
   * Returns the hosted checkout URL the client should redirect to. The
   * actual balance credit happens server-side via the webhook — never trust
   * the redirect for state mutation.
   */
  async createPmCheckoutSession(pmAmount: number): Promise<{ url: string; sessionId: string }> {
    return this.fetchJson(
      '/api/billing/checkout',
      z.object({ url: z.string().url(), sessionId: z.string() }),
      {
        method: 'POST',
        body: JSON.stringify({ pmAmount }),
        retries: 0,
        timeout: 15000,
      }
    );
  }

  // ===========================================================================
  // Referral / invite codes
  // ===========================================================================

  /** Mint a fresh one-use referral code + share link. Not idempotent, each call mints a new code. */
  async generateInviteCode(): Promise<{ code: string; shareUrl: string }> {
    return this.fetchJson(
      '/api/referral/generate-code',
      z.object({ code: z.string(), shareUrl: z.string() }),
      { method: 'POST', retries: 0 }
    );
  }

  /** Redeem a friend's referral code. Idempotent, safe to retry on network failure. */
  async redeemInviteCode(code: string): Promise<{ success: boolean; bonusPm: number }> {
    const result = await this.fetchJson(
      '/api/referral/redeem',
      z.object({ success: z.boolean(), bonusPm: z.number() }),
      { method: 'POST', body: JSON.stringify({ code }) }
    );
    this.clearCache(); // bust cached balance/profile reads so the credited amount shows immediately
    return result;
  }

  // ===========================================================================
  // Social rewards (Discord join + X review)
  // ===========================================================================

  /**
   * Per-reward claim state for the Profile-tab Rewards block. Fetched fresh on
   * mount (cache-buster) since the X-review reward flips to `credited`
   * asynchronously via Tavian's daily pass.
   */
  async getRewardsStatus(): Promise<{
    discord: RewardState;
    xReview: RewardState;
    referralConverted: boolean;
  }> {
    return this.fetchJson(
      `/api/rewards/status?_t=${Date.now()}`,
      z.object({
        discord: RewardStateSchema,
        xReview: RewardStateSchema,
        // Added for the promo-banner invite→Discord handoff. `.default(false)`
        // keeps older API deploys (no field) from failing the parse.
        referralConverted: z.boolean().default(false),
      }),
      { retries: 1, timeout: 5000 }
    );
  }

  /**
   * Finish the Discord-join OAuth handshake: hand the authorization `code` to
   * the server, which exchanges it, checks guild membership, and credits the
   * join reward on a first verified join. `not_member` means they haven't joined yet (no
   * error). `already_claimed` means the reward landed on an earlier call and no
   * balance moved now, so callers must not announce a fresh grant for it.
   * Not idempotent-at-network-level (a fresh `code` is single-use), so
   * `retries: 0`; the server credit itself IS idempotent per uid.
   */
  async discordOAuthCallback(code: string): Promise<{
    status: 'not_member' | 'credited' | 'already_claimed' | 'discord_already_used';
    newBalance?: number;
  }> {
    const result = await this.fetchJson(
      '/api/rewards/discord/callback',
      z.object({
        status: z.enum(['not_member', 'credited', 'already_claimed', 'discord_already_used']),
        newBalance: z.number().optional(),
      }),
      { method: 'POST', body: JSON.stringify({ code }), retries: 0 }
    );
    if (result.status === 'credited') {
      this.clearCache(); // bust cached balance so the new total shows immediately
    }
    return result;
  }

  /**
   * Record the intent to tweet a Polymyth review. The server reads the caller's
   * own character name (never trusted from the client) and returns it so the
   * compose-intent URL and the recorded claim carry the SAME name — that's what
   * Tavian's X pass matches on. `credited` means the async grant already landed.
   */
  async claimXReviewIntent(): Promise<{ status: 'pending' | 'credited'; characterName: string }> {
    return this.fetchJson(
      '/api/rewards/x-review/claim-intent',
      z.object({
        status: z.enum(['pending', 'credited']),
        characterName: z.string(),
      }),
      { method: 'POST', retries: 0 }
    );
  }

  // ===========================================================================
  // Feedback
  // ===========================================================================

  /** Submit beta feedback (authed). Rating is a 1–5 star experience rating. */
  async submitFeedback(text: string, rating?: number): Promise<void> {
    const body: { text: string; rating?: number } = { text };
    if (typeof rating === 'number') body.rating = rating;
    await this.fetchJson('/api/feedback', z.object({ success: z.boolean() }), {
      method: 'POST',
      body: JSON.stringify(body),
      retries: 0,
    });
  }

  // ===========================================================================
  // Health Check
  // ===========================================================================

  async getStatus(): Promise<{ status: string; timestamp: string; version: string }> {
    return this.fetchJson('/api/status', StatusSchema, { skipAuth: true });
  }

  // ===========================================================================
  // User Profile
  // ===========================================================================

  async getMyProfile(): Promise<MyProfile> {
    return this.fetchJson('/api/users/me', MyProfileSchema);
  }

  /**
   * Partial update of the caller's profile in Firestore.
   * Writes `displayName`, `avatarUrl`, `dropBehavior`, and/or the combat
   * highlight preference via
   * PATCH /api/users/me. Server returns `{ success: true }`; we ignore the body.
   */
  async patchMe(updates: {
    displayName?: string;
    avatarUrl?: string;
    dropBehavior?: DropBehavior;
    combatHighlightsEnabled?: boolean;
  }): Promise<{ displayName?: string }> {
    const body: Record<string, string | boolean> = {};
    if (updates.displayName !== undefined) body.displayName = updates.displayName;
    if (updates.avatarUrl !== undefined) body.avatarUrl = updates.avatarUrl;
    if (updates.dropBehavior !== undefined) body.dropBehavior = updates.dropBehavior;
    if (updates.combatHighlightsEnabled !== undefined) {
      body.combatHighlightsEnabled = updates.combatHighlightsEnabled;
    }

    // The server assigns the canonical (unique) username and echoes it back, so a
    // collision rename ('bob' → 'bob2') is reflected by the caller.
    const res = await this.fetchJson(
      '/api/users/me',
      z.looseObject({ success: z.boolean().optional(), displayName: z.string().nullish() }),
      { method: 'PATCH', body: JSON.stringify(body) }
    );
    // Targeted invalidation of the cached GET /api/users/me so the next read
    // reflects the new profile. Cache key format matches fetchJson line 228.
    this.cache.delete('GET:/api/users/me:undefined');
    return { displayName: res.displayName ?? undefined };
  }

  /**
   * Create a real (non-anonymous) account via POST /api/auth/signup. No
   * Firebase session exists yet at call time, hence `skipAuth`. Not
   * idempotent (creates a Firebase Auth user), so no retry. `display_name`
   * is sent snake_case: unlike `turnstile_token`, the backend has no camelCase
   * alias for this field. `turnstileToken` is required whenever the backend's
   * `TURNSTILE_SECRET_KEY` is configured (same captcha gate `guestStart` uses).
   */
  async signUpWithEmail(
    email: string,
    password: string,
    displayName?: string,
    turnstileToken?: string
  ): Promise<{ success: boolean; userId: string; pmTokenBalance?: number }> {
    return this.fetchJson(
      '/api/auth/signup',
      z.object({
        success: z.boolean(),
        userId: z.string(),
        pmTokenBalance: z.number().optional(),
      }),
      {
        method: 'POST',
        skipAuth: true,
        retries: 0,
        body: JSON.stringify({ email, password, display_name: displayName, turnstileToken }),
      }
    );
  }

  /**
   * Provision the guest profile + starter gift via POST /api/guest/start,
   * Turnstile-gated server-side. Sends the chosen username explicitly (so the
   * assigned name doesn't race the token write) and returns the canonical
   * (possibly suffixed) name + granted balance. Idempotent server-side.
   */
  async guestStart(
    username: string,
    turnstileToken?: string
  ): Promise<{ pmTokenBalance?: number; displayName?: string }> {
    const res = await this.fetchJson(
      '/api/guest/start',
      z.looseObject({ pmTokenBalance: z.number().optional(), displayName: z.string().nullish() }),
      { method: 'POST', body: JSON.stringify({ username, turnstileToken }) }
    );
    this.cache.delete('GET:/api/users/me:undefined');
    return { pmTokenBalance: res.pmTokenBalance, displayName: res.displayName ?? undefined };
  }

  /**
   * Delete the caller's Firebase Auth account + Firestore cascade.
   * Body: `{ confirmEmail }` (case-insensitive match enforced server-side).
   */
  async deleteAccount(confirmEmail: string): Promise<void> {
    await this.fetchJson('/api/users/me', z.looseObject({ success: z.boolean().optional() }), {
      method: 'DELETE',
      body: JSON.stringify({ confirmEmail }),
      // Don't retry a destructive op; don't auto-signout on 401 because the
      // server may have already invalidated the token mid-flight.
      retries: 0,
      skipSignOutOn401: true,
    });
  }

  // ===========================================================================
  // Email Subscription API
  // ===========================================================================

  /** GET /api/account/email-subscription — authenticated */
  async getEmailSubscription(): Promise<{ subscribed: boolean }> {
    return this.fetchJson(
      '/api/account/email-subscription',
      z.object({ subscribed: z.boolean() }),
      { retries: 1, timeout: 8000 }
    );
  }

  /** PUT /api/account/email-subscription — authenticated */
  async putEmailSubscription(subscribed: boolean): Promise<{ subscribed: boolean }> {
    return this.fetchJson(
      '/api/account/email-subscription',
      z.object({ subscribed: z.boolean() }),
      {
        method: 'PUT',
        body: JSON.stringify({ subscribed }),
        retries: 0,
        timeout: 8000,
      }
    );
  }

  /**
   * POST /api/auth/send-verification — authenticated. Sends a branded
   * verification email whose action link points at polymyth.quest (not
   * Firebase's firebaseapp.com page). The recipient is the token's email, never
   * client-supplied. Returns `{ ok, alreadyVerified? }`.
   */
  async sendVerificationEmail(): Promise<{ ok: boolean; alreadyVerified?: boolean }> {
    return this.fetchJson(
      '/api/auth/send-verification',
      z.object({ ok: z.boolean(), alreadyVerified: z.boolean().optional() }),
      { method: 'POST', retries: 0, timeout: 10000 }
    );
  }

  /**
   * POST /api/auth/request-password-reset — public (no auth). Sends a branded
   * password-reset email whose action link points at polymyth.quest. The server
   * always returns 200 (whether or not the account exists) to avoid email
   * enumeration, so callers should show a uniform "check your inbox" message.
   */
  async requestPasswordReset(email: string): Promise<{ ok: boolean }> {
    return this.fetchJson('/api/auth/request-password-reset', z.object({ ok: z.boolean() }), {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
      retries: 0,
      timeout: 10000,
    });
  }

  /**
   * POST /api/auth/request-email-change — authenticated. Sends a confirmation
   * email (polymyth.quest action link) to the NEW address. Business outcomes come
   * back as `{ ok: false, error }` (`email_in_use`, `same_email`, `invalid_email`)
   * rather than thrown, so callers branch on the field.
   */
  async requestEmailChange(newEmail: string): Promise<{ ok: boolean; error?: string }> {
    return this.fetchJson(
      '/api/auth/request-email-change',
      z.object({ ok: z.boolean(), error: z.string().optional() }),
      { method: 'POST', body: JSON.stringify({ newEmail }), retries: 0, timeout: 10000 }
    );
  }

  /**
   * POST /api/auth/apply-email-change — public (no auth). Completes an email
   * change/revert from the signed token in the `/auth/action` link. The server
   * applies the change via the Admin SDK (no Firebase auto-notice) and, on a
   * forward change, emails a branded notice to the old address. `action` is
   * `change` or `revert`; business outcomes come back as `{ ok: false, error }`.
   */
  async applyEmailChange(token: string): Promise<{ ok: boolean; error?: string; action?: string }> {
    return this.fetchJson(
      '/api/auth/apply-email-change',
      z.object({ ok: z.boolean(), error: z.string().optional(), action: z.string().optional() }),
      {
        method: 'POST',
        body: JSON.stringify({ token }),
        skipAuth: true,
        retries: 0,
        timeout: 10000,
      }
    );
  }

  // ===========================================================================
  // Admin API
  // ===========================================================================

  async getAdminOverview(): Promise<AdminOverviewResponse> {
    return this.fetchJson('/api/admin/overview', AdminOverviewSchema, {
      retries: 1,
      timeout: 15000,
    });
  }

  async getAdminGameStatus(): Promise<AdminGameStatusResponse> {
    return this.fetchJson('/api/admin/game-status', AdminGameStatusSchema, {
      retries: 1,
      timeout: 10000,
    });
  }

  async getAdminLobbyErrors(): Promise<ErrorEntry[]> {
    return this.fetchJson('/api/admin/lobby-errors', z.array(ErrorEntrySchema), {
      retries: 1,
      timeout: 8000,
    });
  }

  async getServiceHealth(env: 'local' | 'staging' = 'local'): Promise<ServiceHealthResponse> {
    return this.fetchJson(`/api/admin/service-health?env=${env}`, ServiceHealthSchema, {
      retries: 1,
      timeout: 10000,
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
